/**
 * B525 — the Analytics section's doors (`analytics.*`), coarse aggregates
 * only (the funnel's R7 stance: counts leave, never a person's trail).
 *
 * `analytics.funnel` carries the funnel to the shells and the steps: the
 * buyer's own marks (visit → view → checkout) split by surface, then the
 * steps the ORDERS answer (hold taken → paid, and the holds that expired),
 * with the two leaks the ruling makes first-class — abandoned at cart and
 * hold expired — and the window before it in the same shape (comparison
 * windows frame every chart). The unit is the buyer-day throughout. Fenced
 * to the books seat, the same as funnel.read and report.run.
 */
import { z } from "zod";
import { readOp, zEpochMs, zId, zMoney } from "./common.js";

const zCount = z.number().int().min(0);
const zDay = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);

export const zFunnelSurfaceBucket = z.enum(["hosted", "inline", "list", "dialog", "unrecorded"]);

const zFunnelWindow = z
  .object({
    fromDay: zDay,
    toDay: zDay,
    visits: zCount,
    views: zCount,
    checkouts: zCount,
    holds: zCount,
    paid: zCount,
    expired: zCount,
    leaks: z.object({ abandonedAtCart: zCount, holdExpired: zCount }).strict(),
    bySurface: z.array(
      z.object({ surface: zFunnelSurfaceBucket, visits: zCount, views: zCount, checkouts: zCount }).strict(),
    ),
  })
  .strict();

const exampleWindow = (fromDay: string, toDay: string, scale: number) => ({
  fromDay,
  toDay,
  visits: 120 * scale,
  views: 64 * scale,
  checkouts: 20 * scale,
  holds: 14 * scale,
  paid: 11 * scale,
  expired: 3 * scale,
  leaks: { abandonedAtCart: 6 * scale, holdExpired: 3 * scale },
  bySurface: [
    { surface: "hosted" as const, visits: 80 * scale, views: 40 * scale, checkouts: 12 * scale },
    { surface: "dialog" as const, visits: 40 * scale, views: 24 * scale, checkouts: 8 * scale },
  ],
});

export const analyticsFunnel = readOp({
  name: "analytics.funnel",
  permission: "ledger:read",
  principals: ["STAFF:MANAGER", "STAFF:ADMIN"],
  fields: { channelId: zId.optional(), from: zEpochMs, to: zEpochMs },
  response: z.object({ current: zFunnelWindow, prior: zFunnelWindow }).strict(),
  exampleRequest: { from: 1_780_000_000_000, to: 1_780_518_400_000 },
  exampleResponse: {
    current: exampleWindow("2026-05-28", "2026-06-03", 2),
    prior: exampleWindow("2026-05-21", "2026-05-27", 1),
  },
});

/**
 * `analytics.lead_time` — how far ahead buyers book: a sale's tickets placed
 * by the whole days between purchase and departure (a journey's earliest
 * leg), in five buckets that always all answer, with the lower median and
 * the slotless tickets counted apart; the window is the purchase day, the
 * prior window beside it. Every channel unless one is named.
 */
export const zLeadTimeBucket = z.enum(["same-day", "1-2", "3-7", "8-30", "31+"]);

const zLeadTimeWindow = z
  .object({
    fromDay: zDay,
    toDay: zDay,
    tickets: zCount,
    buckets: z.array(z.object({ bucket: zLeadTimeBucket, tickets: zCount }).strict()),
    /** Absent when no ticket had a departure (the membrane carries no null). */
    medianDays: zCount.optional(),
    unslotted: zCount,
  })
  .strict();

const exampleLeadTime = (fromDay: string, toDay: string, scale: number) => ({
  fromDay,
  toDay,
  tickets: 40 * scale,
  buckets: [
    { bucket: "same-day" as const, tickets: 12 * scale },
    { bucket: "1-2" as const, tickets: 8 * scale },
    { bucket: "3-7" as const, tickets: 10 * scale },
    { bucket: "8-30" as const, tickets: 7 * scale },
    { bucket: "31+" as const, tickets: 3 * scale },
  ],
  medianDays: 2,
  unslotted: 5 * scale,
});

export const analyticsLeadTime = readOp({
  name: "analytics.lead_time",
  permission: "ledger:read",
  principals: ["STAFF:MANAGER", "STAFF:ADMIN"],
  fields: { channelId: zId.optional(), from: zEpochMs, to: zEpochMs },
  response: z.object({ current: zLeadTimeWindow, prior: zLeadTimeWindow }).strict(),
  exampleRequest: { from: 1_780_000_000_000, to: 1_780_518_400_000 },
  exampleResponse: {
    current: exampleLeadTime("2026-05-28", "2026-06-03", 2),
    prior: exampleLeadTime("2026-05-21", "2026-05-27", 1),
  },
});

/**
 * `analytics.departures` — departure performance over a SERVICE-day window
 * (the calendar's local days): departures run, capacity and sold from the
 * pools of record (the screen divides — no float crosses), cancelled
 * departures apart, booked revenue per currency on a journey's earliest leg,
 * and the same numbers by service, by local weekday (seven rows, Monday
 * first) and by local hour; the prior window beside it. Raw sold counts
 * cross to the books seat alone (slot.occupancy's audience).
 */
const zLoad = { departures: zCount, capacity: zCount, sold: zCount };
export const zWeekday = z.enum(["mon", "tue", "wed", "thu", "fri", "sat", "sun"]);

const zDeparturesWindow = z
  .object({
    fromDay: zDay,
    toDay: zDay,
    ...zLoad,
    cancelled: zCount,
    revenue: z.array(zMoney),
    byService: z.array(
      z.object({ serviceId: zId, name: z.string().min(1).optional(), ...zLoad, revenue: z.array(zMoney) }).strict(),
    ),
    byWeekday: z.array(z.object({ weekday: zWeekday, ...zLoad }).strict()),
    byHour: z.array(z.object({ hour: z.string().regex(/^\d{2}$/), ...zLoad }).strict()),
  })
  .strict();

const exampleDepartures = (fromDay: string, toDay: string, scale: number) => ({
  fromDay,
  toDay,
  departures: 14,
  capacity: 1400,
  sold: 610 * scale,
  cancelled: 0,
  revenue: [{ amount: 1_830_000 * scale, currency: "USD" }],
  byService: [
    { serviceId: "svc-bay", name: "Bay cruise", departures: 14, capacity: 1400, sold: 610 * scale, revenue: [{ amount: 1_830_000 * scale, currency: "USD" }] },
  ],
  byWeekday: (["mon", "tue", "wed", "thu", "fri", "sat", "sun"] as const).map((weekday) => ({
    weekday,
    departures: 2,
    capacity: 200,
    sold: weekday === "sat" ? 130 * scale : 80 * scale,
  })),
  byHour: [
    { hour: "09", departures: 7, capacity: 700, sold: 250 * scale },
    { hour: "14", departures: 7, capacity: 700, sold: 360 * scale },
  ],
});

export const analyticsDepartures = readOp({
  name: "analytics.departures",
  permission: "ledger:read",
  principals: ["STAFF:MANAGER", "STAFF:ADMIN"],
  fields: { from: zEpochMs, to: zEpochMs },
  response: z.object({ current: zDeparturesWindow, prior: zDeparturesWindow }).strict(),
  exampleRequest: { from: 1_780_000_000_000, to: 1_780_518_400_000 },
  exampleResponse: {
    current: exampleDepartures("2026-05-28", "2026-06-03", 1),
    prior: exampleDepartures("2026-05-21", "2026-05-27", 1),
  },
});
